/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from "react";
import Navbar from "../Navbar/Navbar";
import {fetchPreOrders, updatePreOrderStatus} from "@/api/PreOrdersAPI";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {toast} from "../ui/use-toast";
import {PreOrderModel} from "@/api/Models";
import LoadingElement from "../Loading/LoadingElement";
import {Modal} from "@/components/ui/modal";
import {MdContactPhone} from "react-icons/md";
import ApiHttpClient from "@/HttpClient";

function OrdersValidation() {
    enum Tabs {
        Draft = 68,
        Validated = 72,
        Aborted = 159,
    }

    const tabsToShow: Tabs[] = [Tabs.Draft, Tabs.Validated, Tabs.Aborted];

    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageStatuses, setCurrentPageStatuses] = useState(0);
    const pageSize = 5;
    const [activeTab, setActiveTab] = useState<Tabs | undefined>(Tabs.Draft);
    const [activeAllOrders, setActiveAllOrders] = useState(false);
    const [changedStatusIsClicked, setChangedStatusIsClicked] = useState(false);
    const [, setValidateAll] = useState(false);
    const [toggleContactInfo, setToggleContactInfo] = useState(false);
    const [organizationName, setOrganizationName] = useState('');
    const [organizationPhone, setOrganizationPhone] = useState('');
    const [organizationEmail, setOrganizationEmail] = useState('');
    const [inputValue, setInputValue] = useState(currentPage + 1);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
    const [inputValueStatuses, setInputValueStatuses] = useState(currentPage + 1);
    const [debounceTimerStatuses, setDebounceTimerStatuses] = useState<NodeJS.Timeout | null>(null);

    const queryClient = useQueryClient();

    const handlePageChange = (selectedPage: any) => {
        setCurrentPage(selectedPage);
    };

    const handlePageChangeStatuses = (selectedPage: any) => {
        setCurrentPageStatuses(selectedPage);
    };

    const refreshPageAfterChangeStatus = () => {
        window.location.reload();
    }

    useEffect(() => {
        setInputValue(currentPage + 1);
    }, [currentPage]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);

        if (data?.totalPages && value > data.totalPages) {
            value = data.totalPages;
        } else if (value < 1) {
            value = 1;
        }
        setInputValue(value);

        if (debounceTimer) clearTimeout(debounceTimer);

        const timer = setTimeout(() => {
            if (data?.totalPages && value >= 1 && value <= data?.totalPages) {
                handlePageChange(value - 1);
            }
        }, 500);

        setDebounceTimer(timer);
    };

    useEffect(() => {
        setInputValueStatuses(currentPageStatuses + 1);
    }, [currentPageStatuses]);

    const handleInputChangeStatuses = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);

        if (dataStatuses?.totalPages && value > dataStatuses.totalPages) {
            value = dataStatuses.totalPages;
        } else if (value < 1) {
            value = 1;
        }
        setInputValueStatuses(value);

        if (debounceTimerStatuses) clearTimeout(debounceTimerStatuses);

        const timer = setTimeout(() => {
            if (dataStatuses?.totalPages && value >= 1 && value <= dataStatuses?.totalPages) {
                handlePageChangeStatuses(value - 1);
            }
        }, 500);

        setDebounceTimerStatuses(timer);
    };

    const {data, isLoading, error,} = useQuery({
        queryKey: ["products", currentPage + 1, pageSize],
        queryFn: fetchPreOrders,
    });

    const updatePreOrderStatusMutation = useMutation(
        {
            mutationFn: (params: {
                orderId: number,
                documentStatusId: number
            }) => updatePreOrderStatus(params.orderId, params.documentStatusId),
            onSuccess: () => {
                queryClient.invalidateQueries({queryKey: ["products"], refetchType: "all"});
                toast({description: 'Statusul comenzii a fost actualizat cu succes.'});
                setChangedStatusIsClicked(false);
            },
            onError: (error: any) => {
                toast({description: 'Error updating order status: ' + error.message});
                setChangedStatusIsClicked(false);
            },
        }
    )

    const [checkedState, setCheckedState] = useState(
        new Array(data?.items.length).fill(false)
    );

    const [checkedBoxWithOrderId, setCheckedBoxWithOrderId] = useState<{ [key: string | number]: number }>({});

    useEffect(() => {
        setCurrentPage(0);
        setCurrentPageStatuses(0);
    }, [activeTab]);

    useEffect(() => {
        setCheckedState(new Array(data?.items.length).fill(false));
    }, [data?.items])

    let propertyName = "DocumentStatusId";
    let propertyValue;

    switch (activeTab) {
        case Tabs.Draft:
            propertyValue = [68, 300];
            break;
        case Tabs.Validated:
            propertyValue = [72, 310];
            break;
        case Tabs.Aborted:
            propertyValue = [159];
            break;
        default:
            propertyValue = "";
    }

    const fetchPreOrderByStatuses = async ({queryKey}: any) => {
        const [, propertyName, propertyValues, pageNumber, pageSize] = queryKey;
        const requests = propertyValues.map((value: number) =>
            ApiHttpClient().post('/preOrder/search', {
                "advancedSearch": {
                    "fields": [
                        propertyName,
                    ],
                    "keyword": value.toString(),
                },
                "pageNumber": pageNumber,
                "pageSize": pageSize,
            })
        );

        const results = await Promise.all(requests);

        // combine all items
        const combinedItems = results.flatMap((result) => result.data.items);

        const totalCount = results.reduce((acc, result) => acc + result.data.totalCount, 0);
        const totalPages = Math.max(...results.map((result) => Math.ceil(result.data.totalCount / pageSize)));

        return {
            items: combinedItems,
            totalCount,
            totalPages,
        };
    };

    const {data: dataStatuses, isLoading: isLoadingStatuses, error: errorStatuses} = useQuery({
        queryKey: ["orderByStatus", propertyName, propertyValue, currentPageStatuses + 1, pageSize],
        queryFn: fetchPreOrderByStatuses,
        refetchOnWindowFocus: false,
        //keepPreviousData: true,
    });

    if (isLoading) return <p className="h-screen w-screen flex items-center justify-center"><LoadingElement/></p>;
    if (error || !data) {
        toast({description: "A aparut o eroare la afisarea listei de comenzi."});
        return <></>;
    }

    if (isLoadingStatuses) return <p className="h-screen w-screen flex items-center justify-center"><LoadingElement/>
    </p>;
    if (errorStatuses || !dataStatuses) {
        toast({description: "A aparut o eroare la afisarea listei de comenzi."});
        return <></>;
    }

    const handleTabClick = (tab: Tabs) => {
        setActiveAllOrders(false);
        setActiveTab(tab);
    };

    const handleAllOrdersTabClick = () => {
        setActiveAllOrders(true);
    };

    const handleValidateAll = () => {
        setValidateAll(true);

        for (let [, orderId] of Object.entries(checkedBoxWithOrderId)) {
            updatePreOrderStatusMutation.mutate({orderId: orderId, documentStatusId: 72});
        }

        setValidateAll(false);
    }

    const handleOnCheckForValidation = (position: number, orderId: number) => {

        const updateCheckedState = checkedState.map((item, index) => index === position ? !item : item);

        setCheckedState(updateCheckedState);

        if (updateCheckedState[position] === true) {
            setCheckedBoxWithOrderId(prevState => ({
                ...prevState,
                [position]: orderId
            }));
        } else {
            if (Object.hasOwn(checkedBoxWithOrderId, position)) {
                delete checkedBoxWithOrderId[position];
            }
        }
    }

    return (
        <>
            <Navbar/>
            <div className="relative overflow-x-auto mx-12 text-center mt-6 shadow-md sm:rounded-lg">
                <div className="flex justify-between flex-col sm:flex-row">
                    <ul className="flex flex-wrap text-sm mb-4 font-medium text-center text-gray-500 dark:text-gray-400 sm:flex-wrap">
                        {tabsToShow.map((tab: any) => (
                            <li key={tab} className="me-2 mb-2 sm:mb-0">
                                <div
                                    className={`inline-block px-4 py-3 rounded-lg bg-gray-100 hover:text-gray-900 hover:cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-800 dark:hover:text-red-500 ${activeTab === tab && !activeAllOrders ? 'bg-blue-500 text-red-500' : ''}`}
                                    onClick={() => handleTabClick(tab)}
                                >
                                    {Tabs[tab]}
                                </div>
                            </li>
                        ))}
                        <li className="me-2">
                            <div
                                className={`inline-block px-4 py-3 rounded-lg bg-gray-100 hover:text-gray-900 hover:cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-800 dark:hover:text-red-500 ${activeAllOrders ? 'bg-blue-500 text-red-500' : ''}`}
                                onClick={() => handleAllOrdersTabClick()}
                            >
                                Toate Precomenzile
                            </div>
                        </li>
                    </ul>
                </div>

                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                        <th scope="col" className="p-4">
                            <div className="flex items-center">
                                <button onClick={handleValidateAll}
                                        className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded">
                                    Aproba tot
                                </button>
                            </div>
                        </th>
                        <th scope="col" className="px-6 py-3 bg-gray-50">
                            Numar Precomanda
                        </th>
                        <th scope="col" className="px-6 py-3">
                            Nume Organizatie
                        </th>
                        <th scope="col" className="px-6 py-3">
                            Valoare Precomanda
                        </th>
                        <th scope="col" className="px-6 py-3">
                            Status
                        </th>
                        <th scope="col" className="px-6 py-3">
                            Actiune
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    {!activeAllOrders && dataStatuses.items.map((order: PreOrderModel, index: number) => {
                        return (
                            <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-600">
                                <td className="w-4 p-4">
                                    {(order.documentStatus.documentStatusName === 'Draft' || order.documentStatus.documentStatusName === 'Draft Transit') &&
                                        <div className="flex items-center ">
                                            <input id="checkbox-table-search-1" type="checkbox"
                                                   checked={checkedState[index]}
                                                   onChange={() => handleOnCheckForValidation(index, order.id)}
                                                   className="w-4 h-4 ml-9 text-red-300 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-red-400 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"/>
                                            <label htmlFor="checkbox-table-search-1"
                                                   className="sr-only">checkbox</label>
                                        </div>
                                    }
                                </td>
                                <th scope="row"
                                    className="flex items-center px-3 py-4 bg-white text-gray-900 whitespace-nowrap dark:text-white">
                                    <div className="ps-3">
                                        <div className="text-base font-semibold"><a
                                            href={"/my-account/preorders/pre-order-detail/" + order.id}
                                            className="underline">{order.id}</a></div>
                                        <div className="flex flex-col mt-1">
                                            <div className="font-normal text-gray-500">Tip
                                                precomanda: {order.documentType.documentTypeCode}</div>
                                            <div className="font-normal text-gray-500">Data
                                                precomanda: {order.orderDate.split('T')[0]}</div>
                                        </div>
                                    </div>
                                </th>
                                <td className="px-6 py-4">
                                    <button onClick={(event) => {
                                        event.preventDefault();
                                        setToggleContactInfo(true);
                                        setOrganizationName(order.workingPoint.organization.name);
                                        setOrganizationPhone(order.workingPoint.organization.contactPhone);
                                        setOrganizationEmail(order.workingPoint.organization.contactEmail)
                                    }} className="text-blue-600 font-semibold" data-modal-target="popup-modal"
                                            data-modal-toggle="popup-modal">{order.workingPoint.workingPointName}</button>
                                    {toggleContactInfo &&
                                        <Modal show={toggleContactInfo} onClose={() => setToggleContactInfo(false)}
                                               popup>
                                            <Modal.Header/>
                                            <Modal.Body>
                                                <div className="text-center">
                                                    <MdContactPhone
                                                        className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200"/>
                                                    <h3 className="mb-5 text-m font-normal text-gray-500 dark:text-gray-400">
                                                        Datele de contact pentru organizatia {organizationName}
                                                    </h3>
                                                    <h3 className="mb-5 text-m font-normal text-gray-500 dark:text-gray-400">
                                                        Adresa de email: {organizationEmail}
                                                    </h3>
                                                    <h3 className="mb-5 text-m font-normal text-gray-500 dark:text-gray-400">
                                                        Numar de telefon: {organizationPhone}
                                                    </h3>
                                                    <div className="flex justify-center gap-4">
                                                        <button className="border-red-500 mt-3"
                                                                onClick={() => setToggleContactInfo(false)}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            </Modal.Body>
                                        </Modal>}
                                </td>
                                <td className="px-6 py-4">
                                    {order.totalValue} RON
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center">
                                        {order.documentStatus.documentStatusName === 'Aborted' &&
                                            <div className="flex items-center">
                                                <div className="h-2.5 w-2.5 rounded-full bg-red-500 me-2"></div>
                                                {order.documentStatus.documentStatusName}
                                            </div>
                                        }
                                        {order.documentStatus.documentStatusName === 'Validated' &&
                                            <div className="flex items-center">
                                                <div className="h-2.5 w-2.5 rounded-full bg-green-500 me-2"></div>
                                                {order.documentStatus.documentStatusName}
                                            </div>
                                        }
                                        {(order.documentStatus.documentStatusName === 'Draft' || order.documentStatus.documentStatusName === 'Draft Transit') &&
                                            <div className="flex items-center">
                                                <div className="h-2.5 w-2.5 rounded-full bg-yellow-500 me-2"></div>
                                                {order.documentStatus.documentStatusName}
                                            </div>
                                        }
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    {order.documentStatus.documentStatusName === 'Aborted' &&
                                        <div>
                                            {/* <a href="#" className="font-medium text-blue-600 dark:text-blue-500 hover:underline"></a> */}
                                        </div>
                                    }
                                    {order.documentStatus.documentStatusName === 'Validated' &&
                                        <div>
                                        </div>
                                    }
                                    {(order.documentStatus.documentStatusName === 'Draft') ?
                                        <div className="text-nowrap">
                                            <a
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (!changedStatusIsClicked) {
                                                        setChangedStatusIsClicked(true);
                                                        updatePreOrderStatusMutation.mutate({
                                                            orderId: order.id,
                                                            documentStatusId: 72
                                                        }, {
                                                            // onSettled: () => {
                                                            //   setChangedStatusIsClicked(false); 
                                                            // },
                                                        });
                                                    }
                                                }}
                                                className={`font-medium mr-2 ${changedStatusIsClicked ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 dark:text-blue-500 hover:underline'}`}
                                            > Aproba</a> | <a
                                            href="#"
                                            onClick={changedStatusIsClicked ? undefined : () => {
                                                setChangedStatusIsClicked(true);
                                                updatePreOrderStatusMutation.mutate({
                                                    orderId: order.id,
                                                    documentStatusId: 159
                                                });
                                                refreshPageAfterChangeStatus()
                                            }}
                                            className={`font-medium ml-2 ${changedStatusIsClicked ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 dark:text-red-500 hover:underline'}`}
                                        >Respinge</a>
                                        </div> :
                                        (order.documentStatus.documentStatusName === 'Draft Transit') &&
                                        <div className="text-nowrap">
                                            <a
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (!changedStatusIsClicked) {
                                                        setChangedStatusIsClicked(true);
                                                        updatePreOrderStatusMutation.mutate({
                                                            orderId: order.id,
                                                            documentStatusId: 310
                                                        }, {
                                                            // onSettled: () => {
                                                            //   setChangedStatusIsClicked(false); 
                                                            // },
                                                        });
                                                    }
                                                }}
                                                className={`font-medium mr-2 ${changedStatusIsClicked ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 dark:text-blue-500 hover:underline'}`}
                                            > Aproba</a> | <a
                                            href="#"
                                            onClick={changedStatusIsClicked ? undefined : () => {
                                                setChangedStatusIsClicked(true);
                                                updatePreOrderStatusMutation.mutate({
                                                    orderId: order.id,
                                                    documentStatusId: 159
                                                });
                                                refreshPageAfterChangeStatus()
                                            }}
                                            className={`font-medium ml-2 ${changedStatusIsClicked ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 dark:text-red-500 hover:underline'}`}
                                        >Respinge</a>
                                        </div>
                                    }
                                </td>
                            </tr>
                        );
                    })}
                    {activeAllOrders && data.items.map((order: PreOrderModel, index) => {
                        return (
                            <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-600">
                                <td className="w-4 p-4">
                                    {(order.documentStatus.documentStatusName === 'Draft' || order.documentStatus.documentStatusName === 'Draft Transit') &&
                                        <div className="flex items-center ">
                                            <input id="checkbox-table-search-1" type="checkbox"
                                                   checked={checkedState[index]}
                                                   onChange={() => handleOnCheckForValidation(index, order.id)}
                                                   className="w-4 h-4 ml-9 text-red-300 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-red-400 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"/>
                                            <label htmlFor="checkbox-table-search-1"
                                                   className="sr-only">checkbox</label>
                                        </div>
                                    }
                                </td>
                                <th scope="row"
                                    className="flex items-center px-3 py-4 bg-white text-gray-900 whitespace-nowrap dark:text-white">
                                    <div className="ps-3">
                                        <div className="text-base font-semibold"><a
                                            href={"/my-account/preorders/pre-order-detail/" + order.id}
                                            className="underline">{order.id}</a></div>
                                        <div className="flex flex-col mt-1">
                                            <div className="font-normal text-gray-500">Tip
                                                precomanda: {order.documentType.documentTypeCode}</div>
                                            <div className="font-normal text-gray-500">Data
                                                precomanda: {order.orderDate.split('T')[0]}</div>
                                        </div>
                                    </div>
                                </th>
                                <td className="px-6 py-4">
                                    <button onClick={() => {
                                        setToggleContactInfo(true);
                                        setOrganizationName(order.workingPoint.organization.name);
                                        setOrganizationPhone(order.workingPoint.organization.contactPhone);
                                        setOrganizationEmail(order.workingPoint.organization.contactEmail)
                                    }} className="text-blue-600 font-semibold" data-modal-target="popup-modal"
                                            data-modal-toggle="popup-modal">{order.workingPoint.workingPointName}</button>
                                    {toggleContactInfo &&
                                        <Modal show={toggleContactInfo} onClose={() => setToggleContactInfo(false)}
                                               popup>
                                            <Modal.Header/>
                                            <Modal.Body>
                                                <div className="text-center">
                                                    <MdContactPhone
                                                        className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200"/>
                                                    <h3 className="mb-5 text-m font-normal text-gray-500 dark:text-gray-400">
                                                        Datele de contact pentru organizatia {organizationName}
                                                    </h3>
                                                    <h3 className="mb-5 text-m font-normal text-gray-500 dark:text-gray-400">
                                                        Adresa de email: {organizationEmail}
                                                    </h3>
                                                    <h3 className="mb-5 text-m font-normal text-gray-500 dark:text-gray-400">
                                                        Numar de telefon: {organizationPhone}
                                                    </h3>
                                                    <div className="flex justify-center gap-4">
                                                        <button className="border-red-500 mt-3"
                                                                onClick={() => setToggleContactInfo(false)}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            </Modal.Body>
                                        </Modal>}
                                </td>
                                <td className="px-6 py-4">
                                    {order.totalValue} RON
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center">
                                        {order.documentStatus.documentStatusName === 'Aborted' &&
                                            <div className="flex items-center">
                                                <div className="h-2.5 w-2.5 rounded-full bg-red-500 me-2"></div>
                                                {order.documentStatus.documentStatusName}
                                            </div>
                                        }
                                        {order.documentStatus.documentStatusName === 'Validated' &&
                                            <div className="flex items-center">
                                                <div className="h-2.5 w-2.5 rounded-full bg-green-500 me-2"></div>
                                                {order.documentStatus.documentStatusName}
                                            </div>
                                        }
                                        {order.documentStatus.documentStatusName === 'Draft' &&
                                            <div className="flex items-center">
                                                <div className="h-2.5 w-2.5 rounded-full bg-yellow-500 me-2"></div>
                                                {order.documentStatus.documentStatusName}
                                            </div>
                                        }
                                        {order.documentStatus.documentStatusName === 'Draft Transit' &&
                                            <div className="flex items-center">
                                                <div className="h-2.5 w-2.5 rounded-full bg-yellow-500 me-2"></div>
                                                {order.documentStatus.documentStatusName}
                                            </div>
                                        }
                                        {order.documentStatus.documentStatusName === 'In Picking' &&
                                            <div className="flex items-center">
                                                <div className="h-2.5 w-2.5 rounded-full bg-blue-500 me-2"></div>
                                                {order.documentStatus.documentStatusName}
                                            </div>
                                        }
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    {order.documentStatus.documentStatusName === 'Aborted' &&
                                        <div>
                                            {/* <a href="#" className="font-medium text-blue-600 dark:text-blue-500 hover:underline"></a> */}
                                        </div>
                                    }
                                    {order.documentStatus.documentStatusName === 'Validated' &&
                                        <div>
                                        </div>
                                    }
                                    {(order.documentStatus.documentStatusName === 'Draft') ?
                                        <div className="text-nowrap">
                                            <a
                                                href="#"
                                                onClick={changedStatusIsClicked ? undefined : () => {
                                                    setChangedStatusIsClicked(true);
                                                    updatePreOrderStatusMutation.mutate({
                                                        orderId: order.id,
                                                        documentStatusId: 72
                                                    });
                                                    refreshPageAfterChangeStatus()
                                                }}
                                                className={`font-medium mr-2 ${changedStatusIsClicked ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 dark:text-blue-500 hover:underline'}`}
                                            > Aproba</a> | <a
                                            href="#"
                                            onClick={changedStatusIsClicked ? undefined : () => {
                                                setChangedStatusIsClicked(true);
                                                updatePreOrderStatusMutation.mutate({
                                                    orderId: order.id,
                                                    documentStatusId: 159
                                                });
                                                refreshPageAfterChangeStatus()
                                            }}
                                            className={`font-medium ml-2 ${changedStatusIsClicked ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 dark:text-red-500 hover:underline'}`}
                                        >Respinge</a>
                                        </div> :
                                        (order.documentStatus.documentStatusName === 'Draft Transit') &&
                                        <div className="text-nowrap">
                                            <a
                                                href="#"
                                                onClick={changedStatusIsClicked ? undefined : () => {
                                                    setChangedStatusIsClicked(true);
                                                    updatePreOrderStatusMutation.mutate({
                                                        orderId: order.id,
                                                        documentStatusId: 310
                                                    });
                                                    refreshPageAfterChangeStatus()
                                                }}
                                                className={`font-medium mr-2 ${changedStatusIsClicked ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 dark:text-blue-500 hover:underline'}`}
                                            > Aproba</a> | <a
                                            href="#"
                                            onClick={changedStatusIsClicked ? undefined : () => {
                                                setChangedStatusIsClicked(true);
                                                updatePreOrderStatusMutation.mutate({
                                                    orderId: order.id,
                                                    documentStatusId: 159
                                                });
                                                refreshPageAfterChangeStatus()
                                            }}
                                            className={`font-medium ml-2 ${changedStatusIsClicked ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 dark:text-red-500 hover:underline'}`}
                                        >Respinge</a>
                                        </div>
                                    }
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
            {activeAllOrders && <div className="text-center mt-8 mb-2">
                <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                    <li>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                if (currentPage > 0) handlePageChange(currentPage - 1);
                            }}
                            className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                            aria-label="Previous page"
                            disabled={currentPage === 0}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="size-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                    </li>

                    <li>
                        <label htmlFor="Page" className="flex items-center gap-1">
                            <span className="sr-only">Pagina</span>
                            <input
                                type="number"
                                id="Page"
                                value={inputValue}
                                min={1}
                                max={data.totalPages}
                                onChange={handleInputChange}
                                className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                            />
                            <span className="text-sm">/ {data.totalPages}</span>
                        </label>
                    </li>

                    <li>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                if (currentPage < data.totalPages - 1) handlePageChange(currentPage + 1);
                            }}
                            className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                            aria-label="Next page"
                            disabled={currentPage === data.totalPages - 1}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="size-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                    </li>
                </ul>
            </div>
            }

            {
                !activeAllOrders && <div className="text-center mt-8 mb-2">
                    <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                        <li>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (currentPageStatuses > 0) handlePageChangeStatuses(currentPageStatuses - 1);
                                }}
                                className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                aria-label="Previous page"
                                disabled={currentPageStatuses === 0}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="size-4"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                        </li>

                        <li>
                            <label htmlFor="PageStatuses" className="flex items-center gap-1">
                                <span className="sr-only">Pagina</span>
                                <input
                                    type="number"
                                    id="PageStatuses"
                                    value={inputValueStatuses}
                                    min={1}
                                    max={dataStatuses.totalPages}
                                    onChange={handleInputChangeStatuses}
                                    className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                />
                                <span className="text-sm">/ {dataStatuses.totalPages}</span>
                            </label>
                        </li>

                        <li>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (currentPageStatuses < dataStatuses.totalPages - 1) handlePageChangeStatuses(currentPageStatuses + 1);
                                }}
                                className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                aria-label="Next page"
                                disabled={currentPageStatuses === dataStatuses.totalPages - 1}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="size-4"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                        </li>
                    </ul>
                </div>
            }
        </>
    );
}

export default OrdersValidation;
